import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBriefFoldLook = 'brief-fold-card' | 'brief-fold-row' | 'brief-fold-locked';

const BRIEF_FOLD_LOOKS = new Set<string>(['brief-fold-card', 'brief-fold-row', 'brief-fold-locked']);
export const isWedgeBriefFoldLook = (look: string | undefined): look is WedgeBriefFoldLook => look !== undefined && BRIEF_FOLD_LOOKS.has(look);

/* Released brief fold and locked section controls. The card header and the plain header of a brief fold are one full width flex row
   with no fill and no edge, left aligned text in the Wedge font and the manipulation touch action. The card header has a 13px gap,
   13px 14px of padding and a 62px floor; the plain header has a 10px gap, 2px 0 10px of padding and a 44px floor. A caller passes
   its own padding in style when the section sets one. The locked look is the unlock overlay of a locked section: an absolute box
   over the blurred content, with no edge or padding, a centered column with a 6px gap and the Wedge font. The caller class paints its
   wash and inks. None draws a transparent target or an anchor (wedgeSettingsDrawsHit): each already spans its whole row or section. */
export function wedgeBriefFoldStyle(look: WedgeBriefFoldLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'brief-fold-card': return {
      width: '100%', display: 'flex', alignItems: 'center', gap: 13,
      background: 'none', border: 0, padding: '13px 14px',
      cursor: 'pointer', textAlign: 'left', fontFamily: r('font'),
      minHeight: 62, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
    };
    case 'brief-fold-row': return {
      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
      background: 'none', border: 0, padding: '2px 0 10px',
      cursor: 'pointer', textAlign: 'left', fontFamily: r('font'),
      minHeight: 44, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
    };
    case 'brief-fold-locked': return {
      position: 'absolute', inset: 0,
      border: 0, cursor: 'pointer', padding: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 6, fontFamily: r('font'),
    };
  }
}
